import { Component, computed, effect, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AlertComponent } from '@shared/components/alert';
import { ButtonComponent } from '@shared/components/button';
import { CheckboxComponent } from '@shared/components/checkbox';
import { FieldErrorDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalService } from '@shared/components/modal';
import { AsyncSelectDropdownComponent, asyncOption, type SelectedRef } from '@shared/components/async-select-dropdown';
import { SelectComponent } from '@shared/components/select';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';

import { Paiement, RapprochementManuelForm, Reglement } from './rapprochement-manuel.interface';

import type { Signal } from '@angular/core';
import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import { RapprochementManuelService } from './rapprochement-manuel.service';

import { CustomValidators } from '../../../partage/forms/custom-validators';
import { SourceSelectEnMemoire } from '../../../partage/forms/source-select-en-memoire';
import { suivreFormulaire } from '../../../partage/forms/suivre-formulaire';
import { formaterMontant } from '../../../partage/format/montant';

@Component({
  selector: 'app-rapprochement-manuel',
  imports: [
    ReactiveFormsModule,
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    FormFieldComponent,
    FieldErrorDirective,
    InputComponent,
    SelectComponent,
    AsyncSelectDropdownComponent,
  ],
  templateUrl: './rapprochement-manuel.route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './rapprochement-manuel.route.component.scss',
})
export class RapprochementManuelRouteComponent implements OnInit {
  private rapprochementManuelService = inject(RapprochementManuelService);
  private route = inject(ActivatedRoute);
  private modal = inject(ModalService);
  clients = this.rapprochementManuelService.clients;
  reglements = this.rapprochementManuelService.reglements;
  paiements = this.rapprochementManuelService.paiements;

  rapprochementForm: FormGroup<RapprochementManuelForm>;

  // Lu par le gabarit : erreurs et activation des champs notifiées à chaque validation
  protected readonly formulaire: Signal<FormGroup<RapprochementManuelForm>>;

  public filteredPaiement = this.rapprochementManuelService.filteredPaiement;
  public filteredReglement = this.rapprochementManuelService.filteredReglement;

  constructor() {
    this.rapprochementForm = new FormGroup<RapprochementManuelForm>(
      {
        client: new FormControl(
          { value: null, disabled: false },
          { nonNullable: true, validators: [Validators.required] },
        ),
        reglement: new FormControl(
          {
            value: null,
            disabled: true,
          },
          [Validators.required],
        ),
        paiement: new FormControl({
          value: null,
          disabled: true,
        }),
        commentaire: new FormControl(
          {
            value: null,
            disabled: true,
          },
          [Validators.required, Validators.maxLength(200), CustomValidators.noWhitespaceValidator()],
        ),
        nonRappochable: new FormControl<boolean>(
          {
            value: false,
            disabled: true,
          },
          { nonNullable: true },
        ),
      },
      {
        validators: [this.controleGlobale(), this.controleMontantReglementEtPaiement()],
      },
    );

    this.formulaire = suivreFormulaire(this.rapprochementForm);

    this.rapprochementForm.controls.client.valueChanges.subscribe(client => {
      this.rapprochementManuelService.paiementSelection.set(undefined);
      this.rapprochementManuelService.reglementSelection.set(undefined);
      if (client) {
        this.rapprochementForm.controls.paiement.enable();
        this.rapprochementForm.controls.reglement.enable();
      }
      this.rapprochementManuelService.clientSelection.set(client ?? undefined);
    });

    this.rapprochementForm.controls.paiement.valueChanges.subscribe(paiement => {
      this.rapprochementManuelService.paiementSelection.set(paiement ?? undefined);
    });
    this.rapprochementForm.controls.reglement.valueChanges.subscribe(reglement => {
      this.rapprochementManuelService.reglementSelection.set(reglement === null ? undefined : reglement);
    });
    this.rapprochementForm.controls.commentaire.valueChanges.subscribe(commentaire => {
      this.rapprochementManuelService.commentaire.set(commentaire === null ? undefined : commentaire);
    });
    this.rapprochementForm.controls.nonRappochable.valueChanges.subscribe(nonRappochable =>
      nonRappochable || this.rapprochementForm.controls.client.value === null
        ? this.rapprochementForm.controls.paiement.disable()
        : this.rapprochementForm.controls.paiement.enable(),
    );

    effect(() => {
      this.rapprochementForm.controls.client.patchValue(this.rapprochementManuelService.clientSelection() || null, {
        emitEvent: false,
      });
      this.rapprochementForm.controls.paiement.patchValue(this.rapprochementManuelService.paiementSelection() || null, {
        emitEvent: false,
      });
      this.rapprochementForm.controls.reglement.patchValue(
        this.rapprochementManuelService.reglementSelection() || null,
        { emitEvent: false },
      );

      if (this.rapprochementManuelService.estNonRappro()) {
        this.rapprochementForm.controls.nonRappochable.enable();
      } else {
        this.rapprochementForm.controls.nonRappochable.disable();
      }
      if (this.rapprochementManuelService.clientSelection()) {
        this.rapprochementForm.controls.reglement.enable();
        this.rapprochementForm.controls.paiement.enable();
        this.rapprochementForm.controls.commentaire.enable();
      } else {
        this.rapprochementForm.controls.reglement.disable();
        this.rapprochementForm.controls.paiement.disable();
        this.rapprochementForm.controls.commentaire.disable();
      }
      if (this.rapprochementManuelService.resetForm()) {
        this.resetForm();
      }
    });
  }

  // ── Ponts vers `app-select` ──────────────────────────────────────────────
  // `app-select` travaille sur des CLÉS et porte sa propre recherche. Les listes
  // sources restent celles du service, qui conserve le filtrage CROISÉ (règlement
  // restreint par le paiement retenu, et inversement) — logique métier, pas recherche.

  // La clé est le CODE client, pas son libellé : deux clients homonymes se
  // départageaient sinon par `find`, qui retenait toujours le premier des deux
  protected optionsClients = computed<SelectOption[]>(() =>
    (this.clients() ?? []).map(client => ({ value: client.codeClient, label: client.libelleClient })),
  );

  protected clientsSelectionnes = computed<string[]>(() => {
    const client = this.rapprochementManuelService.clientSelection();
    return client ? [client.codeClient] : [];
  });

  protected choisirClient(valeurs: readonly string[]): void {
    const code = valeurs[0];
    const client = code ? (this.clients() ?? []).find(c => c.codeClient === code) : undefined;
    this.rapprochementForm.controls.client.setValue(client ?? null);
  }

  // La référence bancaire peut être vide : l'identifiant Aparté sert alors de clé
  private cleReglement = (reglement: Reglement): string =>
    reglement.referenceBancaire || String(reglement.idFinancialAparte);

  // ── Règlements et paiements : `app-async-select-dropdown` ────────────────
  // Listes de plusieurs dizaines de milliers de lignes : le sélecteur n'en rend que 20 par page,
  // servies depuis la liste déjà chargée. Il est recréé par le template à chaque nouvelle liste.
  protected sourceReglements = new SourceSelectEnMemoire<Reglement>(
    () => this.filteredReglement() ?? [],
    reglement => this.cleReglement(reglement),
  );

  protected optionReglement = (reglement: Reglement) =>
    asyncOption({ id: reglement.idReglement, label: this.cleReglement(reglement) });

  protected reglementSelectionne = computed<SelectedRef<Reglement>>(() => {
    const reglement = this.rapprochementManuelService.reglementSelection();
    return reglement ? { kind: 'item', item: reglement } : null;
  });

  protected choisirReglement(reglement: Reglement | null): void {
    this.rapprochementForm.controls.reglement.setValue(reglement);
  }

  protected sourcePaiements = new SourceSelectEnMemoire<Paiement>(
    () => this.filteredPaiement() ?? [],
    paiement => paiement.referenceBancaire,
  );

  // Clé `idPaiement` : la référence n'est pas unique. Pas de repli, l'API ne renvoie jamais de référence vide.
  protected optionPaiement = (paiement: Paiement) =>
    asyncOption({ id: paiement.idPaiement, label: paiement.referenceBancaire });

  protected paiementSelectionne = computed<SelectedRef<Paiement>>(() => {
    const paiement = this.rapprochementManuelService.paiementSelection();
    return paiement ? { kind: 'item', item: paiement } : null;
  });

  protected choisirPaiement(paiement: Paiement | null): void {
    this.rapprochementForm.controls.paiement.setValue(paiement);
  }

  /* Champs d'affichage seul : `app-input` attend une chaîne. Le montant passe par le
     formateur central comme partout ailleurs (BAC-737) — un `String(…)` rendait `1234.5`.

     La chaîne VIDE est conservée quand rien n'est sélectionné, et n'est pas remplacée par
     le `—` du formateur : ici l'absence signifie « aucun règlement choisi », pas « montant
     inconnu ». Un tiret dans un formulaire encore vierge se lirait comme une donnée
     manquante. */
  protected montantReglement = computed(() => {
    const reglement = this.rapprochementManuelService.reglementSelection();
    return reglement ? formaterMontant(reglement.montantEnEuros) : '';
  });

  protected montantPaiement = computed(() => {
    const paiement = this.rapprochementManuelService.paiementSelection();
    return paiement ? formaterMontant(paiement.montantEnEuros) : '';
  });

  // Les validateurs lisent `getRawValue()` : « Non rapprochable » désactive le paiement, que `value` omettrait

  // Montants différents : le serveur refuserait le rapprochement. Sans objet en non rapprochable
  controleMontantReglementEtPaiement(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { reglement, paiement, nonRappochable } = (control as FormGroup<RapprochementManuelForm>).getRawValue();
      if (nonRappochable || !reglement || !paiement) {
        return null;
      }
      return reglement.montantEnEuros === paiement.montantEnEuros ? null : { controleMontantReglementEtPaiement: true };
    };
  }

  // Un règlement, et soit un paiement à lui rapprocher, soit la case « Non rapprochable »
  controleGlobale(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const { reglement, paiement, nonRappochable } = (control as FormGroup<RapprochementManuelForm>).getRawValue();
      return reglement && (nonRappochable || paiement) ? null : { controleGlobale: true };
    };
  }

  openDialog() {
    this.rapprochementForm?.markAllAsTouched();

    if (this.rapprochementForm.valid) {
      const confirmation = this.rapprochementForm.controls.nonRappochable.value
        ? {
            title: 'Mettre en non rapprochable',
            message: 'Vous êtes sur le point de mettre le règlement en non rapprochable',
            confirmLabel: 'Valider la mise en non rapprochable',
            cancelLabel: 'Annuler',
          }
        : {
            title: 'Valider le rapprochement',
            message: 'Vous êtes sur le point de valider un rappochement manuel',
            confirmLabel: 'Valider le rapprochement',
            cancelLabel: 'Annuler le rapprochement',
          };

      // Action engageante mais réversible : ton par défaut, pas `danger`
      this.modal.confirm(confirmation).subscribe(result => {
        if (result) {
          if (this.rapprochementForm.controls.nonRappochable.value) {
            this.rapprochementManuelService.nonRapprochable.set(true);
          } else {
            this.rapprochementManuelService.faireRapprochementManuel.set(true);
          }
        }
      });
    }
  }

  telechargerFichier(typeFichier: string) {
    this.rapprochementManuelService.typeFichier.set(typeFichier);
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe((params: ParamMap) => {
      this.rapprochementManuelService.urlClient.set(params.get('codeClient') || '');
      const c = params.get('codeClient');
      const r = params.get('idReglement');
      const p = params.get('idPaiement');
      const t = params.get('type');
      if (c) {
        this.rapprochementManuelService.urlClient.set(c);
      }
      if (r) {
        this.rapprochementManuelService.urlReg.set(r);
      }
      if (p) {
        this.rapprochementManuelService.urlPaie.set(p);
      }
      if (t) {
        this.telechargerFichier(t);
      }
    });
  }

  resetForm() {
    this.rapprochementForm.controls.paiement.reset();
    this.rapprochementForm.controls.reglement.reset();
    this.rapprochementForm.controls.client.reset();
    this.rapprochementForm.controls.commentaire.reset();
    this.rapprochementForm.controls.nonRappochable.setValue(false);
    this.rapprochementForm.controls.paiement.disable();
    this.rapprochementForm.controls.reglement.disable();
    this.rapprochementForm.controls.commentaire.disable();
    this.rapprochementForm.controls.nonRappochable.disable();
    this.rapprochementManuelService.resetForm.set(false);
  }
}
